import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import React, { useEffect } from 'react';
import { VOICE_TO_CHART_COPY as COPY } from '../sandboxCopy';
import FullEhrFeaturePill from './FullEhrFeaturePill';

interface VoiceToChartDemoModalProps {
  onClose: () => void;
}

/**
 * The Voice-to-Chart demonstration, simplified to the tester-provided designs
 * (2026-09-08): abbreviated patient conversation → automatically generated
 * Subjective, side by side on desktop and stacked on mobile, with the AI
 * callout beneath. Optional; closes straight back to the Subjective
 * walkthrough.
 */
const VoiceToChartDemoModal: React.FC<VoiceToChartDemoModalProps> = ({ onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={COPY.title}
    >
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative flex max-h-[94vh] w-full sm:max-w-3xl flex-col overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-5 sm:p-7 shadow-2xl dark:bg-dark-gray-800 sm:m-4">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          ✕
        </button>

        <div className="flex items-start gap-4 pr-8">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full ring-2 ring-primary-200 dark:ring-primary-800">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-white">
              <Icon icon={Icons.microphone} className="h-5 w-5" />
            </span>
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{COPY.title}</h3>
              <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-[10px] font-bold tracking-wide text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                {COPY.optionalBadge}
              </span>
              <FullEhrFeaturePill />
            </div>
            <p className="mt-1 text-base font-bold leading-snug text-gray-900 dark:text-gray-100">
              {COPY.headline}
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">{COPY.body}</p>
          </div>
        </div>

        <div className="mt-5 flex flex-col items-stretch gap-3 md:grid md:grid-cols-[1fr_auto_1fr] md:items-center">
          <div className="rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3 dark:border-dark-gray-600 dark:bg-dark-gray-700/40 md:self-stretch">
            <p className="text-sm text-gray-900 dark:text-gray-100">
              <span className="font-bold">{COPY.conversationLabel}</span>{' '}
              <span className="font-normal text-gray-500 dark:text-gray-400">
                {COPY.conversationTag}
              </span>
            </p>
            <div className="mt-2 space-y-1.5">
              {COPY.transcript.map((entry, i) => (
                <p key={i} className="grid grid-cols-[3.5rem_1fr] gap-1 text-xs leading-relaxed">
                  <span
                    className={`font-semibold ${
                      entry.speaker === 'Doctor'
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-amber-600 dark:text-amber-400'
                    }`}
                  >
                    {entry.speaker}:
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">{entry.line}</span>
                </p>
              ))}
              <p className="text-center text-xs italic text-gray-400 md:hidden">
                {COPY.conversationContinues}
              </p>
            </div>
          </div>

          <span
            aria-hidden
            className="self-center text-2xl font-bold text-primary-600 dark:text-primary-400"
          >
            <span className="hidden md:inline">→</span>
            <span className="md:hidden">↓</span>
          </span>

          <div className="rounded-xl border border-primary-200 bg-primary-50/50 px-4 py-3 dark:border-primary-800 dark:bg-primary-900/15 md:self-stretch">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-gray-900 dark:text-gray-100">
                <span className="font-bold">{COPY.subjectiveLabel}</span>{' '}
                <span className="font-semibold text-primary-600 dark:text-primary-300">
                  {COPY.subjectiveTag}
                </span>
              </p>
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-primary-500 text-primary-600 dark:text-primary-300 md:hidden">
                <Icon icon={Icons.check} className="h-3.5 w-3.5" />
              </span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-gray-700 dark:text-gray-200">
              {COPY.subjectiveText}
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-primary-50 px-4 py-3 ring-1 ring-inset ring-primary-200/70 dark:bg-primary-900/20 dark:ring-primary-800">
          <Icon
            icon={Icons.sparkles}
            className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-300"
          />
          <p className="text-sm text-gray-700 dark:text-gray-200">
            <span className="font-semibold text-primary-800 dark:text-primary-200">
              {COPY.aiCallout}
            </span>{' '}
            {COPY.aiCalloutDetail}
          </p>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto rounded-lg bg-primary-600 px-5 py-3 sm:py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
          >
            {COPY.cta}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceToChartDemoModal;
